import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  GroupMemberDto,
  CreateGroupMemberDto,
  ResponseDto,
} from "@mimber/schema"
import { groupMembersKeys } from "../group-members.utils"
import { memberGroupsKeys } from "../../member-groups/member-groups.utils"

interface AddGroupMemberOptions extends Omit<
  UseMutationOptions<ResponseDto<GroupMemberDto>, Error, CreateGroupMemberDto>,
  "mutationFn"
> {}

export const useAddGroupMember = (
  groupUuid: string,
  options: AddGroupMemberOptions = {}
): UseMutationResult<
  ResponseDto<GroupMemberDto>,
  Error,
  CreateGroupMemberDto
> => {
  guardAppUsage("mimber-organization", "useAddGroupMember")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: CreateGroupMemberDto) => {
      const response = await server.post<ResponseDto<GroupMemberDto>>(
        `groups/${groupUuid}/members`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: groupMembersKeys.findAll(groupUuid),
      })
      queryClient.invalidateQueries({
        queryKey: memberGroupsKeys.findMemberGroups(variables.membershipUuid),
      })
      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
